import { Group } from '@mantine/core';
import { useRouter } from 'next/router';
import { match } from 'ts-pattern';
import { IconBarrierBlock } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import { Button, ButtonLink } from '~/domains/design-system/Button';

import { FullScreenMessage } from '../FullScreenMessage/FullScreenMessage';

type UnknownErrorScreenProps = {
  statusCode: 500 | 404 | 403 | 401 | 400 | number;
  isShowHomeButton?: boolean;
  hideActions?: boolean;
};

function UnknownErrorScreen({
  isShowHomeButton,
  statusCode,
  hideActions = false,
}: UnknownErrorScreenProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  const { title, description } = match(statusCode)
    .with(400, () => ({
      title: t('errors.BAD_REQUEST.title'),
      description: t('errors.BAD_REQUEST.body'),
    }))
    .with(401, () => ({
      title: t('errors.UNAUTHORIZED.title'),
      description: t('errors.UNAUTHORIZED.body'),
    }))
    .with(403, () => ({
      title: t('errors.FORBIDDEN.title'),
      description: t('errors.FORBIDDEN.body'),
    }))
    .with(404, () => ({
      title: t('errors.NOT_FOUND.title'),
      description: t('errors.NOT_FOUND.body'),
    }))
    .with(500, () => ({
      title: t('errors.INTERNAL_SERVER_ERROR.title'),
      description: t('errors.INTERNAL_SERVER_ERROR.body'),
    }))
    .otherwise(() => ({
      title: t('errors.INTERNAL_SERVER_ERROR.title'),
      description: t('errors.INTERNAL_SERVER_ERROR.body'),
    }));

  return (
    <FullScreenMessage
      icon={<IconBarrierBlock />}
      title={title}
      subtitle={description}
      actions={
        !hideActions && (
          <Group>
            <Button
              key="reload"
              onClick={() => {
                router.reload();
              }}
              size="medium"
              variant="secondary-fill"
            >
              {t('errors.actions.reload')}
            </Button>
            {isShowHomeButton && (
              <ButtonLink size="medium" key="home" variant="secondary-stroke" href="/">
                {t('errors.actions.takeMeHome')}
              </ButtonLink>
            )}
          </Group>
        )
      }
    />
  );
}

export default UnknownErrorScreen;
